import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { fetchTimbradoDe, type AlvoDoTimbrado } from '@/api/timbrado';
import {
  EditorDeTimbrado,
  PreviaDaFolha,
  novoTimbrado,
  type ModoDoTimbrado,
  type Timbrado,
} from '@/components/editor-forense';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { chaveDoTimbrado, gravarTimbrado, timbradoDoServidor } from '@/lib/editor/timbradoEditor';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { queryPersistMeta } from '@/lib/pwa/queryPersistence';

// Os modos que o servidor guarda: texto com logotipo, ou as faixas prontas.
const MODOS: ModoDoTimbrado[] = ['texto', 'imagem'];

const doServidor = (c: Parameters<typeof timbradoDoServidor>[0]): Timbrado => timbradoDoServidor(c) ?? novoTimbrado('texto');

/**
 * O papel timbrado guardado de um alvo — o pessoal (Configurações → Papel timbrado)
 * ou o de um escritório (a aba Peças dele). O timbrado se edita com o MESMO editor do
 * painel do timbrado do editor de peças (`EditorDeTimbrado`) e grava pelo mesmo
 * caminho (`gravarTimbrado`); aqui ficam a marca d'água, que é dos documentos
 * gerados, e o "Salvar". Sem `podeEditar`, só a folha em miniatura.
 */
export function FormularioDoTimbrado({ alvo, podeEditar = true }: { alvo: AlvoDoTimbrado; podeEditar?: boolean }) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: chaveDoTimbrado(alvo),
    queryFn: () => fetchTimbradoDe(alvo),
    // Relê ao abrir: outra tela (o editor, outro sócio) pode ter mudado o timbrado.
    refetchOnMount: 'always',
    // O pessoal fica no aparelho (offline); o do escritório, não (logo e faixas de outrem).
    meta: alvo === 'me' ? queryPersistMeta.nonEmpty : undefined,
  });

  const [t, setT] = useState<Timbrado | null>(null);
  const [marcaDagua, setMarcaDagua] = useState('');

  // Hidrata uma vez, quando o timbrado chega do servidor (ou a releitura não pode
  // acontecer — sem rede, fica o do cache): o que muda depois não reescreve o que se edita.
  if (data && !t && !isFetching) {
    setT(doServidor(data));
    setMarcaDagua(data.watermark ?? '');
  }

  const salvar = useMutation({
    mutationFn: (atual: Timbrado) => gravarTimbrado(queryClient, atual, { marcaDagua, alvo }),
    onSuccess: async (salvo) => {
      // Re-sincroniza com o que o servidor gravou (trim, vazios, faixas).
      setT(doServidor(salvo));
      setMarcaDagua(salvo.watermark ?? '');
      // O resolvido (o que o PDF e a folha aplicam) muda com o pessoal e com o do escritório.
      await queryClient.invalidateQueries({ queryKey: [...queryKeys.timbrado.resolvido] });
      showSuccess('Timbrado salvo.');
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  if (isLoading || (!isError && !t)) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size={32} label="Carregando timbrado" />
      </div>
    );
  }
  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{classifyError(error).message}</AlertDescription>
      </Alert>
    );
  }
  if (!podeEditar) {
    return (
      <div className="max-w-xs">
        <PreviaDaFolha t={t} marcaDagua={marcaDagua} margens={data?.pagina?.margens} />
      </div>
    );
  }
  const idDaMarca = `timbrado-watermark-${alvo === 'me' ? 'me' : alvo.escritorio}`;
  return (
    <div className="flex flex-col gap-5">
      <EditorDeTimbrado
        t={t}
        aoMudar={setT}
        modos={MODOS}
        marcaDagua={marcaDagua}
        depois={
          <div className="flex flex-col gap-2">
            <Label htmlFor={idDaMarca}>Marca d’água</Label>
            <Input id={idDaMarca} value={marcaDagua} onChange={(e) => setMarcaDagua(e.target.value)} placeholder="Ex.: MINUTA" />
            <p className="text-xs text-muted-foreground">Texto opcional exibido em diagonal ao fundo do documento.</p>
          </div>
        }
      />
      <div className="flex justify-end">
        <Button onClick={() => t && salvar.mutate(t)} disabled={salvar.isPending}>
          {salvar.isPending ? (
            <>
              <Spinner size={16} className="mr-2 text-inherit" />
              Salvando...
            </>
          ) : (
            'Salvar timbrado'
          )}
        </Button>
      </div>
    </div>
  );
}
